import React, {useCallback} from "react";
import {Box, Card, Skeleton, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Switch} from "../../../base/switch";
import CustomerAutocomplete from "../../../text-fields/autocomplete/customer-autocomplete";
import {Alert} from "@mui/lab";
import {DuplicateOrderVariants} from "../index";
import useAccessLevels from "../../../../hooks/use-access-levels";

const DuplicateOrderDialogConfigureSection = ({flags, setFlags, variant, loadingSource}) => {
    const operatorAccessLevels = useAccessLevels();

    /**
     * Sets the default flags based on the variant.
     */
    React.useEffect(() => {
        setFlags(prevState => ({
            ...prevState,
            ...(!(variant === DuplicateOrderVariants.Quotes || variant === DuplicateOrderVariants.SalesOrders) && {
                bypass: false,
            }),
            ...(!(variant === DuplicateOrderVariants.SalesOrders || variant === DuplicateOrderVariants.Invoices || variant === DuplicateOrderVariants.Credits) && {
                transformToSalesOrder: false,
                dimensions: false,
                ...(!operatorAccessLevels.packages.sales.quoteFreightInfo && {
                    shippingInfo: false,
                }),
            }),
        }))
    }, [variant, setFlags, operatorAccessLevels.packages.sales.quoteFreightInfo])

    /**
     * Toggles the flag.
     * @param key
     * @param value
     */
    const toggleFlag = useCallback((key, value) => {
        setFlags(prev => ({...prev, [key]: value}));
    }, [setFlags]);

    /**
     *
     * @type {(function(*, *): void)|*}
     */
    const onCustomerChanged = useCallback((e, newCustomer) => {
        toggleFlag('newCustomer', newCustomer);
    }, [toggleFlag]);

    return (
        <>
            <Box sx={{paddingInline: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <Typography variant={'subtitle1'}>
                    Create A New Order From an Existing Record.
                </Typography>
                <Typography variant={'body2'}>
                    Select the information you wish to carry over to the new duplicate quote.
                </Typography>
                {
                    loadingSource &&
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                        <Grid2 xs={12}>
                            <Skeleton width="100%" height={60} sx={{transform: 'none'}}/>
                        </Grid2>
                    </Grid2>
                }
                <Grid2 container spacing={2}>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={'Original Customer'}
                            enabledDescription={"The new order will retain the same customer as the original. You can change that by toggling this field to allow for a new customer."}
                            disabledDescription={"The new order will be for the customer of your selection. If you want to retain the same customer, toggle this field."}
                            enabled={flags.customer}
                            toggleEnabled={(val) => toggleFlag('customer', val)}
                        >
                            {
                                !flags.customer &&
                                <CustomerAutocomplete
                                    textFieldProps={{
                                        size: 'small',
                                    }}
                                    fullWidth
                                    label={`New Customer`}
                                    value={flags.newCustomer}
                                    onChange={onCustomerChanged}
                                />
                            }
                        </ConfigurationCard>
                    </Grid2>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={"Order Details"}
                            enabledDescription={"The new order will retain some/all of the order details from the original order. You can change that by toggling this field to allow for a new order details."}
                            disabledDescription={"The new order will be for the customer of your selection. If you want to retain the same customer, toggle this field."}
                            enabled={Object.values(flags.orderInfo).some(Boolean)}
                            toggleEnabled={(val) => {
                                const newFlags = {po: val, fob: val, requiredDate: val, referenceNo: val, territory: val, accountManager: val};
                                toggleFlag('orderInfo', newFlags);
                            }}
                        >
                            {
                                Object.values(flags.orderInfo).some(Boolean) &&
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    {
                                        Object.keys(flags.orderInfo).map((key) => (
                                            <Box key={key} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',}}>
                                                <Typography variant={'caption'} component={'p'} fontWeight={600}>
                                                    {
                                                        key === 'po' ? 'PO Number' :
                                                            key === 'fob' ? 'FOB' :
                                                                key === 'requiredDate' ? 'Required Date' :
                                                                    key === 'referenceNo' ? 'Reference Number' :
                                                                        key === 'territory' ? 'Territory'
                                                                            : key === "accountManager" ? 'Account Manager'
                                                                                : key === "paymentTerms" ? 'Payment Terms'
                                                                                    : ""
                                                    }
                                                </Typography>
                                                <Switch
                                                    size={5}
                                                    color={'primary'}
                                                    checked={flags.orderInfo[key]}
                                                    onChange={(val) => toggleFlag('orderInfo', {...flags.orderInfo, [key]: val})}
                                                />
                                            </Box>
                                        ))
                                    }
                                </Box>
                            }
                        </ConfigurationCard>
                    </Grid2>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={'Products'}
                            enabledDescription={"The new order will retain the same products as the original. You can change that by toggling this field to allow for a new products."}
                            disabledDescription={"The new order will be instantiated with no products. If you want to retain the same products, toggle this field."}
                            enabled={flags.products}
                            toggleEnabled={(val) => toggleFlag('products', val)}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={'Notes'}
                            enabledDescription={"The new order will retain the same notes as the original. You can change that by toggling this field to allow for a new notes."}
                            disabledDescription={"The new order will be instantiated with no notes. If you want to retain the same notes, toggle this field."}
                            enabled={flags.notes}
                            toggleEnabled={(val) => toggleFlag('notes', val)}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={'Shipping Address'}
                            enabledDescription={"The new order will retain the same shipping address as the original. You can change that by toggling this field to allow for using the default shipping address of the customer associated with this order."}
                            disabledDescription={"The new order will be instantiated with the default shipping address of the customer associated with this order."}
                            enabled={flags.shippingAddress}
                            toggleEnabled={(val) => toggleFlag('shippingAddress', val)}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <ConfigurationCard
                            title={'Billing Address'}
                            enabledDescription={"The new order will retain the same billing address as the original. You can change that by toggling this field to allow for using the default shipping address of the customer associated with this order."}
                            disabledDescription={"The new order will be instantiated with the default billing address of the customer associated with this order."}
                            enabled={flags.billingAddress}
                            toggleEnabled={(val) => toggleFlag('billingAddress', val)}
                        />
                    </Grid2>
                    {
                        (variant === DuplicateOrderVariants.Quotes || variant === DuplicateOrderVariants.SalesOrders) &&
                        <Grid2 xs={12}>
                            <ConfigurationCard
                                title={'Bypassed'}
                                enabledDescription={"The new order will retain the same bypass information as the original. You can change that by toggling this field to allow for bypassing the original order."}
                                disabledDescription={"The new order will not be bypassed"}
                                enabled={flags.bypass}
                                toggleEnabled={(val) => toggleFlag('bypass', val)}
                            />
                        </Grid2>
                    }
                    {
                        !!operatorAccessLevels.packages.sales.quoteFreightInfo &&
                        <Grid2 xs={12}>
                            <ConfigurationCard
                                title={'Shipping Info (Freight)'}
                                enabledDescription={"The order will retain the same shipping info as the original. You can change that by toggling this field"}
                                disabledDescription={"The order will not have any shipping info."}
                                enabled={flags.shippingInfo}
                                toggleEnabled={(val) => toggleFlag('shippingInfo', val)}
                            />
                        </Grid2>
                    }
                    {
                        (variant === DuplicateOrderVariants.SalesOrders || variant === DuplicateOrderVariants.Invoices || variant === DuplicateOrderVariants.Credits) &&
                        <>
                            <Grid2 xs={12}>
                                <ConfigurationCard
                                    title={'Generate Sales Order'}
                                    enabledDescription={"The new order will be a sales order. You can change that by toggling this field to allow for generating a sales order."}
                                    disabledDescription={"The new order will remain as a quote."}
                                    enabled={flags.transformToSalesOrder}
                                    toggleEnabled={(val) => toggleFlag('transformToSalesOrder', val)}
                                />
                            </Grid2>
                            {
                                !operatorAccessLevels.packages.sales.quoteFreightInfo &&
                                <Grid2 xs={12}>
                                    <ConfigurationCard
                                        title={'Shipping Info (Freight)'}
                                        enabledDescription={"The new sales order will retain the same shipping info as the original. You can change that by toggling this field to allow for generating a sales order."}
                                        disabledDescription={"The new sales order will not have any shipping info."}
                                        disabled={!flags.transformToSalesOrder}
                                        disableWarning={"Only in Sales-orders, we can set the shipping info. If you want, please toggle the \"Generate Sales Order\" option above"}
                                        enabled={flags.shippingInfo}
                                        toggleEnabled={(val) => toggleFlag('shippingInfo', val)}
                                    />
                                </Grid2>
                            }
                            <Grid2 xs={12}>
                                <ConfigurationCard
                                    title={'Pallets For Dimensions'}
                                    enabledDescription={"The new sales order will retain the same pallets as the original. You can change that by toggling this field to allow for generating a sales order."}
                                    disabledDescription={"The new sales order will not have any pallets"}
                                    disabled={!flags.transformToSalesOrder}
                                    disableWarning={"Only in Sales-orders, we can set the pallets. If you want, please toggle the \"Generate Sales Order\" option above"}
                                    enabled={flags.dimensions}
                                    toggleEnabled={(val) => toggleFlag('dimensions', val)}
                                />
                            </Grid2>
                        </>
                    }
                </Grid2>
            </Box>
        </>
    );
};

const ConfigurationCard = ({title, enabledDescription, disabledDescription, enabled, toggleEnabled, children, disabled, disableWarning}) => {

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 2, display: 'flex', flexDirection: 'column', gap: 1, backgroundColor: disabled ? 'transparent' : 'background.default'}}>
                {
                    disabled && (disableWarning?.length) &&
                    <Alert severity={'warning'} sx={{border: theme => `1px solid ${theme.palette.warning.main}`,}}>
                        <Typography variant={'caption'} fontWeight={600} color={'inherit'}>
                            {disableWarning ?? ""}
                        </Typography>
                    </Alert>
                }
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',}}>
                    <Typography variant={'body1'} sx={{fontWeight: 600}} color={'text.primary'}>
                        {title ?? ""}
                    </Typography>
                    <Switch
                        color={'primary'}
                        checked={enabled}
                        disabled={disabled}
                        onChange={(val) => toggleEnabled(val)}
                    />
                </Box>
                <Typography variant={'body2'} color={'text.secondary'}>
                    {enabled ? enabledDescription : disabledDescription}
                </Typography>
                {children}
            </Card>
        </>
    );
}

export default DuplicateOrderDialogConfigureSection;
